import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import PaymentFormErrorMessage from '@components/checkout/BillingPaymentFormErrorMessage';
import Colors from '@components/shared/Colors';
import { useEmailCapture } from '@utils/useEmailCapture';
import Button from '../Button';
import Spinner from '../Spinner';
import SuccessMessage from '../SuccessMessage';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface Props {
  id: string;
  background?: string;
  textColor?: string;
}

const Waitlist: React.FC<Props> = ({ id, background, textColor = Colors.navyBlue }) => {
  const { t } = useTranslation();
  const { email, setEmail, error, loading, successful, isValidEmail, handleAddToNewsletter } = useEmailCapture(id);

  return (
    <Container $background={background} $textColor={textColor}>
      <ControlsWrap>
        <InputWrap>
          <TextField
            placeholder={t('components.email-address-placeholder-2')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ opacity: successful ? 0.5 : 1.0 }}
            autoCapitalize="none"
            autoCorrect="none"
          />
        </InputWrap>
        <ButtonWrap>
          <WaitlistButton
            $hoverColor={textColor}
            onClick={handleAddToNewsletter}
            disabled={!isValidEmail || loading || successful || error.length > 0}
          >
            {loading ? <Spinner /> : t('components.join-waitlist').toUpperCase()}
          </WaitlistButton>
        </ButtonWrap>
      </ControlsWrap>
      <AgreedTextWrap>
        <Typography $variant={TextVariant.BodyRegularSM}>{t('components.waitlist-promotions-agreed')}</Typography>
      </AgreedTextWrap>
      {(successful || error) && <div style={{ height: '20px' }} />}
      {successful && <SuccessMessage message={t('components.join-waitlist-success')} />}
      {error && <PaymentFormErrorMessage errorMessage={error} />}
    </Container>
  );
};

export default Waitlist;

interface StyleProps {
  $background?: string;
  $textColor?: string;
}

interface ButtonProps {
  $hoverColor?: string;
}

const Container = styled.div<StyleProps>`
  background: ${({ $background }) => $background || 'transparent'};
  color: ${({ $textColor }) => $textColor};
  width: 100%;
  display: flex;
  flex-direction: column;

  input {
    color: ${({ $textColor }) => $textColor};
  }
`;
const ControlsWrap = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const InputWrap = styled.div`
  flex-grow: 1;
`;

const ButtonWrap = styled.div`
  button {
    height: 48px;
  }
`;

const TextField = styled.input`
  background: ${Colors.cream};
  border: 1px solid ${Colors.paleGold};
  box-sizing: border-box;
  border-radius: 2px;
  padding-left: 15px;
  ${TEXT_STYLES[TextVariant.BodyRegularLG]}
  height: 48px;
  opacity: 0.5;
  width: 100%;

  @media (max-width: 768px) {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]}
  }

  &:focus {
    outline: none;
  }
`;

const AgreedTextWrap = styled.div`
  opacity: 0.5;
  margin-top: 16px;
  text-align: left;
`;

const WaitlistButton = styled(Button)<ButtonProps>`
  &:hover {
    background: ${({ $hoverColor }) => $hoverColor};
  }

  @media (max-width: 500px) {
    width: 100%;
  }
`;
